import { ActionTree, GetterTree, MutationTree, Module } from 'vuex';
import { RootState } from '../interfaces/rootState';
import { postWithAuthPayload } from '@/util/HTTPCommpix';
import {
  PadipixBalanceAsset,
  PadipixBalanceResponse,
  PadipixBalanceState
} from '@/store/interfaces/commpix-api/padipix';

const state: PadipixBalanceState = {
  balance: null,
  enabled: true,
  error: false,
};

const mutations: MutationTree<PadipixBalanceState> = {
  setBalance(state, balance: PadipixBalanceAsset[]) {
    state.balance = balance;
    state.enabled = true;
    state.error = false;
  },
  // Tenant has no Manteca user (404) — hide the widget. Not a transient failure.
  setDisabled(state) {
    state.enabled = false;
    state.error = false;
    state.balance = null;
  },
  setError(state) {
    state.error = true;
    state.balance = [];
  },
};

const actions: ActionTree<PadipixBalanceState, RootState> = {
  async fetchPadipixBalance({ commit, }) {
    try {
      const response = await postWithAuthPayload<PadipixBalanceResponse>('/manteca/balance');
      const map = (response.data && response.data.balance) || {};
      // `balance` is a map -> render as an array. Keep raw string amounts; do not
      // parse to a number. An empty map is a valid zero state (also the shape of
      // a transient Manteca outage), rendered the same way by design.
      const balance: PadipixBalanceAsset[] = Object.entries(map).map(
        ([asset, value]) => ({ asset, value, })
      );
      commit('setBalance', balance);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      // 404 -> tenant not Manteca-enabled: hide the widget, no toast.
      if (status === 404) {
        commit('setDisabled');
        return;
      }
      // 401 -> session problem; let the HTTP interceptor / auth flow handle it.
      if (status === 401) {
        throw error;
      }
      // 400 / network / anything else -> generic error state (no 5xx outage
      // branch exists for this endpoint by design).
      commit('setError');
    }
  },
};

const getters: GetterTree<PadipixBalanceState, RootState> = {
  padipixBalance: state => state.balance,
  padipixBalanceEnabled: state => state.enabled,
  padipixBalanceError: state => state.error,
};

export const padipix: Module<PadipixBalanceState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default padipix;
